import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card } from '../../../components/ui/card';
import { 
  DollarSign, Calculator, Building2, Users, Clock, 
  FileText, CreditCard, AlertCircle, CheckCircle, 
  TrendingUp, Settings, Play, Banknote, Link2, Send
} from 'lucide-react';
import { payrollAPI } from '../../../services/api';

const PayrollDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalAreas: 0,
    activeAreas: 0,
    pendingRuns: 0,
    completedRuns: 0,
    totalGross: 0,
    totalNet: 0,
    totalTax: 0,
    totalSS: 0,
    employeesProcessed: 0,
    pendingApprovals: 0,
    pendingTransfers: 0,
    recentRuns: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await payrollAPI.getDashboard();
      const data = response.data;
      setStats({
        totalAreas: data.total_payroll_areas || 0,
        activeAreas: data.active_payroll_areas || 0,
        pendingRuns: data.pending_runs || 0,
        completedRuns: data.completed_runs || 0,
        totalGross: data.total_gross_current_month || 0,
        totalNet: data.total_net_current_month || 0,
        totalTax: data.total_tax_current_month || 0,
        totalSS: data.total_ss_current_month || 0,
        employeesProcessed: data.employees_processed_current_month || 0,
        pendingApprovals: data.pending_approvals || 0,
        pendingTransfers: data.pending_bank_transfers || 0,
        recentRuns: data.recent_runs || []
      });
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const modules = [
    {
      title: 'Payroll Runs',
      description: 'Create and execute payroll runs, view results',
      icon: Play,
      color: 'bg-orange-500',
      path: '/hcm/payroll/runs',
      stat: stats.pendingRuns,
      statLabel: 'Pending Runs',
      statColor: stats.pendingRuns > 0 ? 'text-orange-600' : 'text-gray-900'
    },
    {
      title: 'Payroll Results',
      description: 'View employee payroll calculations and payslips',
      icon: FileText,
      color: 'bg-green-500',
      path: '/hcm/payroll/results',
      stat: stats.employeesProcessed,
      statLabel: 'Processed This Month'
    },
    {
      title: 'Bank Transfers',
      description: 'Manage bank transfer batches for payments',
      icon: CreditCard,
      color: 'bg-orange-500',
      path: '/hcm/payroll/bank-transfers',
      stat: stats.pendingTransfers,
      statLabel: 'Pending Transfers',
      statColor: stats.pendingTransfers > 0 ? 'text-orange-600' : 'text-gray-900'
    },
    {
      title: 'Configuration',
      description: 'Configure payroll areas, tax rates, and deductions',
      icon: Settings,
      color: 'bg-gray-600',
      path: '/hcm/payroll/config',
      stat: stats.activeAreas,
      statLabel: 'Payroll Areas'
    },
    {
      title: 'GL Account Mapping',
      description: 'Configure payroll to finance GL account mappings',
      icon: Link2,
      color: 'bg-orange-500',
      path: '/hcm/payroll/gl-mapping',
      stat: '-',
      statLabel: 'Finance Integration'
    },
    {
      title: 'GL Posting',
      description: 'Post payroll results to the General Ledger',
      icon: Send,
      color: 'bg-orange-500',
      path: '/hcm/payroll/gl-posting',
      stat: '-',
      statLabel: 'Post to Finance'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'bg-gray-100 text-gray-700',
      'RELEASED': 'bg-orange-100 text-orange-700',
      'PROCESSING': 'bg-yellow-100 text-yellow-700',
      'CHECK_RESULTS': 'bg-orange-100 text-orange-700',
      'COMPLETED': 'bg-green-100 text-green-700',
      'POSTED': 'bg-orange-100 text-orange-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="payroll-dashboard">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span>Payroll Accounting</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll Accounting</h1>
          <p className="text-gray-600 mt-1">Process payroll, manage payments, and track compensation</p>
        </div>

        {/* Financial Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-800">Gross Payroll (Month)</p>
                <p className="text-xl font-bold text-green-900">
                  {loading ? '-' : formatCurrency(stats.totalGross)}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-orange-50 to-orange-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500 rounded-lg">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-orange-800">Net Payroll (Month)</p>
                <p className="text-xl font-bold text-orange-900">
                  {loading ? '-' : formatCurrency(stats.totalNet)}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-red-50 to-red-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500 rounded-lg">
                <Calculator className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-red-800">Total Taxes (Month)</p>
                <p className="text-xl font-bold text-red-900">
                  {loading ? '-' : formatCurrency(stats.totalTax)}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-orange-50 to-orange-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500 rounded-lg">
                <Banknote className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-orange-800">Social Security (Month)</p>
                <p className="text-xl font-bold text-orange-900">
                  {loading ? '-' : formatCurrency(stats.totalSS)}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending Approvals</p>
                <p className={`text-xl font-semibold ${stats.pendingApprovals > 0 ? 'text-orange-600' : ''}`}>
                  {loading ? '-' : stats.pendingApprovals}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Completed Runs</p>
                <p className="text-xl font-semibold text-green-600">
                  {loading ? '-' : stats.completedRuns}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Users className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Employees Processed</p>
                <p className="text-xl font-semibold">
                  {loading ? '-' : stats.employeesProcessed}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Building2 className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Payroll Areas</p>
                <p className="text-xl font-semibold">
                  {loading ? '-' : stats.activeAreas}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Module Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {modules.map((module, idx) => (
            <Card 
              key={idx} 
              className="p-6 hover:shadow-lg transition-shadow cursor-pointer" 
              onClick={() => navigate(module.path)}
              data-testid={`payroll-module-${idx}`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${module.color}`}>
                  <module.icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{module.title}</h2>
                      <p className="text-sm text-gray-600 mt-1">{module.description}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${module.statColor || 'text-gray-900'}`}>
                        {loading ? '-' : module.stat}
                      </p>
                      <p className="text-xs text-gray-500">{module.statLabel}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Recent Runs */}
        {stats.recentRuns.length > 0 && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Payroll Runs</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Run Number</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Payroll Area</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Period</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Employees</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Total Net</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {stats.recentRuns.map((run) => (
                    <tr 
                      key={run.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/hcm/payroll/runs/${run.id}`)}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-orange-600">{run.run_number}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{run.payroll_area_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{run.period_year}/{run.period_number}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{run.run_type}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{run.employee_count || 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatCurrency(run.total_net || 0)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(run.status)}`}>
                          {run.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Quick Actions */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button 
              onClick={() => navigate('/hcm/payroll/runs')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
              data-testid="action-create-run"
            >
              <Play className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium">Create Payroll Run</span>
            </button>
            <button 
              onClick={() => navigate('/hcm/payroll/results')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
              data-testid="action-view-results"
            >
              <FileText className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium">View Payslips</span>
            </button>
            <button 
              onClick={() => navigate('/hcm/payroll/bank-transfers')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
              data-testid="action-bank-transfers"
            >
              <CreditCard className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium">Bank Transfers</span>
            </button>
            <button 
              onClick={() => navigate('/hcm/payroll/config')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
              data-testid="action-configure"
            >
              <Settings className="h-5 w-5 text-gray-600" />
              <span className="text-sm font-medium">Configuration</span>
            </button>
            <button 
              onClick={() => navigate('/hcm/payroll/gl-mapping')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
              data-testid="action-gl-mapping"
            >
              <Link2 className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium">GL Mapping</span>
            </button>
            <button 
              onClick={() => navigate('/hcm/payroll/gl-posting')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
              data-testid="action-gl-posting"
            >
              <Send className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium">Post to GL</span>
            </button>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default PayrollDashboard;
